import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AggFunc,
  BaseComponent,
  BaseEditFormComponent,
  ColourReferenceService,
  FormatService,
  GridComponent,
  GridRowSelect,
  GridService,
  ModalService,
  ProductDetails,
  ProductService,
  SalesOrderLineAllocationsSummaryTop,
  SalesOrderLineService,
  SalesOrderLineSummaryTop,
  SalesOrderService,
  SalesOrderStockAllocation,
  SalesOrderStockAllocationService,
  StockItemAllocations,
  StockItemService,
  SummaryItem,
  YesNoModalComponent,
} from 'tms-lib';
import { StockEnquiryStockAllocationsModalComponent, StockItemCommentsComponent } from '../../stock';

@Component({
  selector: 'app-sales-order-line-allocations',
  templateUrl: './sales-order-line-allocations.component.html',
  styleUrl: './sales-order-line-allocations.component.scss',
})
export class SalesOrderLineAllocationsComponent extends BaseComponent implements OnInit {
  @ViewChild(BaseEditFormComponent) form: BaseEditFormComponent;
  @ViewChild(GridComponent, { static: true }) grid: GridComponent;

  @Input() salesOrderLineId: string;
  @Input() header: string;
  @Input() gridCssClass = 'tab-grid';
  @Input() fromSalesOrder = true;
  @Input() salesOrderLine: SalesOrderLineSummaryTop;
  @Output()
  modelUpdated = new EventEmitter();

  model$: Observable<SalesOrderLineAllocationsSummaryTop>;
  stockItems$: Observable<SalesOrderStockAllocation[]>;

  productDetails: ProductDetails;

  columnDefs: ColDef[] = [
    ...this.gridService.checkboxRowSelect(
      'stockNo',
      $localize`Stock Number`,
      (model: SalesOrderStockAllocation) =>
        (model.holdStockAtProductLevel ? model.picked < model.qtyRequired : model.picked === 0) &&
        (!model.hardAllocation || this.fromSalesOrder),
      (model: SalesOrderStockAllocation) =>
        (model.holdStockAtProductLevel ? model.picked < model.qtyRequired : model.picked === 0) &&
        (!model.hardAllocation || this.fromSalesOrder),
    ),
    this.gridService.commentButton((model: SalesOrderStockAllocation) => this.addComment(model)),
    this.gridService.text('batchNo', $localize`Batch Number`),
    this.gridService.text('grade', $localize`Grade`),
    this.gridService.numberFormat('balance', $localize`Current Quantity`, { aggFunc: AggFunc.sum }),
    this.gridService.clickableField(
      'totalAllocated',
      $localize`Total Allocated`,
      (model: SalesOrderStockAllocation) => this.showAllocations(model),
      (model: SalesOrderStockAllocation) => model.totalAllocated > 0,
      { aggFunc: AggFunc.sum },
    ),
    this.gridService.numberFormat('freeStock', $localize`Available`, { aggFunc: AggFunc.sum }),
    this.gridService.editableNumberFormat(
      'allocated',
      $localize`This Allocation`,
      (model: SalesOrderStockAllocation) => this.allocatedEdited(model),
      (model: SalesOrderStockAllocation) =>
        !model.allocateFullPieces && +model.allocated > 0 && (!model.hardAllocation || this.fromSalesOrder),
      { aggFunc: AggFunc.sum },
      undefined,
      3,
    ),
    this.gridService.numberFormat(
      'allocatedImperial',
      $localize`Allocated (Imperial)`,
      { aggFunc: AggFunc.sum, showColumnFunc: (rowData: SalesOrderStockAllocation[]) => this.showColumn(rowData) },
      undefined,
      3,
    ),
    this.gridService.numberFormat('picked', $localize`Picked`, { aggFunc: AggFunc.sum }, undefined, 3),
    this.gridService.text('warehouseKey', $localize`Warehouse Key`),
    this.gridService.text('locationKey', $localize`Location Key`),
  ];

  constructor(
    private stockItemService: StockItemService,
    private salesOrderLineService: SalesOrderLineService,
    private salesOrderStockAllocationService: SalesOrderStockAllocationService,
    private salesOrderService: SalesOrderService,
    private productService: ProductService,
    private colourService: ColourReferenceService,
    private gridService: GridService,
    private formatService: FormatService,
    private modalService: ModalService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getModel();

    if (this.fromSalesOrder) {
      this.columnDefs.push(
        this.gridService.checkbox(
          'hardAllocation',
          $localize`Hard Allocation`,
          (model: SalesOrderStockAllocation) => this.updateHardAllocation(model),
          (model: SalesOrderStockAllocation) => model.allocated > 0,
        ),
      );
    } else {
      this.columnDefs.push(this.gridService.text('hardAllocationYesNo', $localize`Hard Allocation`));
      this.getProductDetails(this.salesOrderLine?.productId);
    }
  }

  summaryItems(model: SalesOrderLineAllocationsSummaryTop): SummaryItem[] {
    return [
      { label: $localize`Product`, value: model.productKey },
      { label: $localize`Description`, value: model.productDescription },
      { label: $localize`Quantity`, value: this.formatService.number(model.quantity, 2) },
      { label: $localize`Allocated`, value: this.formatService.number(model.allocated, 2) },
      { label: $localize`Picked`, value: this.formatService.number(model.picked, 2) },
      { label: $localize`Delivery Date`, value: this.formatService.date(model.deliveryDate) },
    ];
  }

  getModel() {
    this.model$ = this.salesOrderLineService.allocationsSummaryTop(this.salesOrderLineId);
    this.stockItems$ = this.stockItemService.salesOrderStockAllocation(this.salesOrderLineId);
  }

  updateSuccess() {
    this.getModel();
    this.modelUpdated.emit();

    this.salesOrderService.refreshDetails.next();
    this.salesOrderLineService.refreshDetails.next();
    this.stockItemService.refreshDetails.next();
  }

  rowSelected(event: GridRowSelect) {
    const allocateOrDeallocate = event.selected
      ? this.salesOrderStockAllocationService.allocate(
          this.salesOrderLineId,
          event.model.stockItemId,
          event.model.hardAllocation,
        true
        )
      : this.salesOrderStockAllocationService.delete(event.model.id);

    allocateOrDeallocate.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateSuccess());
  }

  private showAllocations(model: SalesOrderStockAllocation) {
    this.stockItemService
      .stockItemAllocations(model.stockItemId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((stockItem: StockItemAllocations) => {
        const { modal, component } = this.modalService.openLarge<StockEnquiryStockAllocationsModalComponent>(
          StockEnquiryStockAllocationsModalComponent,
        );
        component.model = stockItem;
        component.showDelete = false;
        modal.result.then(
          () => {},
          () => {},
        );
      });
  }

  private allocatedEdited(model: SalesOrderStockAllocation) {
    if (model.allowFocQuantity && model.allocated > model.qtyRequired) {
      const { modal, component } = this.modalService.openMedium<YesNoModalComponent>(YesNoModalComponent);
      component.header = $localize`Sales Order Stock Allocation`;
      component.message = $localize`Allocated more than order quantity. Is the extra free of charge?`;
      modal.result.then(
        (update: boolean) => this.updateAllocation(model, update),
        () => {},
      );
    } else {
      this.updateAllocation(model, false);
    }
  }

  private updateAllocation(model: SalesOrderStockAllocation, focQuantity: boolean) {
    this.salesOrderStockAllocationService
      .updateSalesOrderStockAllocation(model.id, model.allocated, focQuantity)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateSuccess());
  }

  private getProductDetails(productId: string) {
    if (!productId) {
      return;
    }
    this.productService
      .edit(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((productDetails: ProductDetails) => (this.productDetails = productDetails));
  }

  private updateHardAllocation(model: SalesOrderStockAllocation) {
    this.salesOrderStockAllocationService
      .updateHardAllocation(model.id, model.hardAllocation)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateSuccess());
  }

  private showColumn(rowData: SalesOrderStockAllocation[]): boolean {
    return rowData.some((x: SalesOrderStockAllocation) => x.imperial);
  }

  addComment(model: SalesOrderStockAllocation) {
    const { modal, component } = this.modalService.openLarge<StockItemCommentsComponent>(StockItemCommentsComponent);
    component.id = model.stockItemId;
    component.isModal = true;
    component.showBack = true;
    modal.result.then(
      () => this.getModel(),
      () => {},
    );
  }
}
